/**
 * Kennzeichnung eines geerbten Sendungsdokuments (Sammelsendung, Task 6) - eine kleine, optisch
 * gedaempfte Fusszeile unter einer Dokumentzeile: „aus Sendung DSV-4711, gilt für 3 Bestellungen".
 *
 * NICHT zu verwechseln mit `Dokumentzeile.tsx` (dem generischen Zeilen-Wrapper mit
 * Tieflink-Hervorhebung, gemeinsam genutzt von `Checkliste.tsx`/`DokumenteTab.tsx` fuer
 * Hervorhebung/`data-testid`). Diese Komponente hier ergaenzt NUR den Hinweistext, sie ersetzt
 * `Dokumentzeile.tsx` nicht und wird zusaetzlich INNERHALB von dessen `children` gerendert.
 *
 * `viaShipmentNo`/`geteiltMit` kommen aus `backend/src/domaene/dokumente.ts#ladeBelegeJeBestellung`
 * (LEFT JOIN auf `shipments`, LATERAL-Zaehlung ueber `document_links.document_id`) - AUSDRUECKLICH
 * NICHT aus `documents/repository.ts#ladeDokumente`: die bleibt bei `DISTINCT ON (doc_type)` fuer
 * die Checkliste (siehe deren Docstring) und wuerde ein geerbtes Dokument neben einem eigenen
 * DESSELBEN Typs verschlucken - genau der Fall, den diese Kennzeichnung sichtbar machen soll
 * (`bl_hawbl`: eigenes House-B/L UND geerbtes Master-B/L stehen nebeneinander).
 *
 * BERICHTIGT (Fixrunde 1 zu Task 6, Review-Befund 2): diese Datei hiess zuerst `DokumentZeileGeerbt.tsx`
 * und exportierte `DokumentZeile` - der Task-Brief nennt die Komponente woertlich so, aber der Name
 * war aus zwei Gruenden falsch: (1) `DokumentZeile.tsx` (Brief-Wortlaut) kollidiert auf diesem
 * (Windows-)Dateisystem mit der bereits bestehenden `Dokumentzeile.tsx` (fuer diesen Ordner ist
 * Gross-/Kleinschreibung laut `fsutil file queryCaseSensitiveInfo` DEAKTIVIERT - beide Namen treffen
 * dieselbe Datei), UND (2) selbst der abweichende Dateiname `DokumentZeileGeerbt.tsx` exportierte
 * `DokumentZeile` - im gesamten `components/`-Baum die einzige Datei, deren Export nicht dem
 * Dateinamen entspricht, UND ein Name, der nicht beschreibt, was gerendert wird (die FUSSZEILE,
 * nicht die Zeile selbst). `GeerbtHinweis` (Datei UND Export) loest alle drei Probleme auf einmal:
 * keine Kollision, Export=Dateiname wie ueberall sonst, und ein Name, der die Fusszeile beschreibt.
 *
 * Bewusst NUR die Fusszeile, keine vollstaendige Zeilendarstellung (Symbol/Name/Link/Loeschen-Knopf):
 * die eigentliche Zeile (Name+Link+Aktionen) steht bereits inline in `Checkliste.tsx`/
 * `DokumenteTab.tsx` (bis Ticket H auch in `ZahlungTab.tsx`) - diese Komponente ergaenzt dort nur den fehlenden
 * Herkunftshinweis, ohne die bereits getestete DOM-Struktur (`dokumenteTabStruktur.test.tsx`)
 * umzubauen.
 */
import type { OrderDocument } from "../../../lib/types.ts";

export interface GeerbtHinweisProps {
  dokument: OrderDocument;
}

export function GeerbtHinweis({ dokument }: GeerbtHinweisProps) {
  if (dokument.viaGroupId && dokument.groupLabel) {
    return <span className="doc-geerbt-hinweis">{dokument.groupLabel}</span>;
  }
  const viaShipmentNo = dokument.viaShipmentNo;
  if (!viaShipmentNo) return null;
  const geteiltMit = dokument.geteiltMit;
  // "gilt für 1 Bestellungen" waere eine Verrenkung: der Normalfall n=1 nennt die Sendung, tut aber
  // nicht so, als sei etwas geteilt - deshalb erst AB > 1 den Zusatz anhaengen.
  const zusatz = typeof geteiltMit === "number" && geteiltMit > 1 ? `, gilt für ${geteiltMit} Bestellungen` : "";
  return (
    <span className="doc-geerbt-hinweis">
      aus Sendung {String(viaShipmentNo)}
      {zusatz}
    </span>
  );
}
